import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FaSearch, FaLightbulb, FaChartLine, FaShieldAlt,
  FaNetworkWired, FaServer, FaExclamationTriangle, FaFileAlt,
  FaRocket, FaCog, FaCalendarAlt, FaCheckCircle, FaEye, FaUsers,
  FaTrophy, FaHdd, FaProjectDiagram, FaBullseye,
  FaPhoneAlt
} from 'react-icons/fa';
import { useScrollAnimation } from '../../hooks';
import { useLanguage } from '../../contexts/LanguageContext';
import { Helmet } from 'react-helmet-async';

const AnalyseConseil = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const [setBannerRef, bannerInView]           = useScrollAnimation(0.1);
  const [setIntroRef, introInView]             = useScrollAnimation(0.1);
  const [setApproachRef, approachInView]       = useScrollAnimation(0.1);
  const [setEvaluationsRef, evaluationsInView] = useScrollAnimation(0.1);
  const [setDeliverablesRef, deliverablesInView] = useScrollAnimation(0.1);

  const bannerImages = ['/images/datacenter.webp', '/images/network.avif', '/images/security.webp', '/images/server1.jpg'];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => setCurrentImageIndex((prev) => (prev + 1) % bannerImages.length), 4000);
    return () => clearInterval(interval);
  }, [bannerImages.length]);

  // Services principaux (Diagnostic / Conseil)
  const mainServices = [
    { 
        icon: <FaSearch />, 
        title: t.analyse.mainServices[0].title, 
        description: t.analyse.mainServices[0].description, 
        color: '#0ea5e9', 
        image: '/images/audit.jpg' 
    },
    { 
        icon: <FaLightbulb />, 
        title: t.analyse.mainServices[1].title, 
        description: t.analyse.mainServices[1].description, 
        color: '#f59e42', 
        image: '/images/conseil.jpg' 
    },
  ];

  // Étapes de l'approche
  const approachIcons = [<FaCalendarAlt />, <FaSearch />, <FaExclamationTriangle />, <FaFileAlt />];
  const approachSteps = t.analyse.approach.steps.map((step, index) => ({
    ...step,
    step: `0${index + 1}`,
    icon: approachIcons[index]
  }));

  // Domaines d'évaluation (DataCenter, Sécurité, Réseau)
  const evaluationAssets = [
    { icon: <FaServer />, color: '#0ea5e9', image: '/images/datacenter.webp' },
    { icon: <FaShieldAlt />, color: '#dc2626', image: '/images/security.webp' },
    { icon: <FaNetworkWired />, color: '#059669', image: '/images/network.avif' },
  ];

  const evaluationAreas = t.analyse.evaluations.items.map((item, index) => ({
    ...item,
    ...evaluationAssets[index]
  }));

  // Livrables
  const deliverableIcons = [<FaChartLine />, <FaExclamationTriangle />, <FaShieldAlt />, <FaSearch />, <FaHdd />, <FaProjectDiagram />, <FaRocket />, <FaCog />];
  const deliverablesList = t.analyse.deliverables.items.map((item, index) => ({
    ...item,
    icon: deliverableIcons[index]
  }));

  // Processus de conseil
  const processIcons = [<FaEye />, <FaFileAlt />, <FaProjectDiagram />, <FaUsers />];
  const processSteps = t.analyse.conseil.process.steps.map((step, index) => ({
    ...step,
    step: `0${index + 1}`,
    icon: processIcons[index]
  }));

  // Avantages
  const advantageIcons = [<FaTrophy />, <FaUsers />, <FaBullseye />, <FaRocket />];
  const advantagesList = t.analyse.conseil.advantages.items.map((adv, index) => ({
    ...adv,
    icon: advantageIcons[index]
  }));

  return (
    <div>
      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.analyse.seo.title}</title>
        <meta name="description" content={t.analyse.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/services/analyse-conseil" />
        
        {/* Open Graph pour LinkedIn (très important pour le conseil B2B) */}
        <meta property="og:title" content={t.analyse.seo.title} />
        <meta property="og:description" content={t.analyse.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/audit.jpg" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* ---------------------------------- */}


      {/* ── BANNER ── */}
      <section ref={setBannerRef} style={{ position: 'relative', height: '70vh', overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', inset: 0 }}>
          {bannerImages.map((image, index) => (
            <div key={index} style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.4)), url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: index === currentImageIndex ? 1 : 0, transition: 'opacity 1s ease-in-out' }} />
          ))}
        </div>
        <Container style={{ position: 'relative', zIndex: 2 }}>
          <Row className="justify-content-center text-center">
            <Col lg={10}>
              <motion.div initial={{ opacity: 0, y: 50 }} animate={bannerInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <motion.h1 style={{ color: '#fff', fontSize: '3.5rem', fontWeight: 800, marginBottom: '1.5rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
                  {lang === 'fr' ? (
                    <>{t.analyse.hero.title} <span style={{ color: '#d90000' }}>IT</span></>
                  ) : (
                    <><span style={{ color: '#d90000' }}>IT</span> {t.analyse.hero.title.replace('IT ', '')}</>
                  )}
                </motion.h1>
                <motion.p style={{ color: 'rgba(255,255,255,0.95)', fontSize: '1.4rem', fontWeight: 500, marginBottom: '2rem', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}>
                  {t.analyse.hero.subtitle}
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={bannerInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.4 }}>
                  <Button onClick={() => navigate('/#contact')} style={{ background: 'linear-gradient(135deg, #d90000, #ff4444)', border: 'none', borderRadius: '25px', padding: '12px 30px', fontSize: '1.1rem', fontWeight: 600, boxShadow: '0 4px 15px rgba(217,0,0,0.3)' }}>
                    <FaPhoneAlt style={{ marginRight: '8px' }} />
                    {t.analyse.hero.cta}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── INTRO ── */}
      <section ref={setIntroRef} style={{ padding: '5rem 0', background: '#f8f9fa' }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, x: -50 }} animate={introInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }}>
                <h2 style={{ fontSize: '2.5rem', fontWeight: 700, color: '#333', marginBottom: '2rem' }}>
                  {t.analyse.intro.title}
                </h2>
                <p style={{ fontSize: '1.2rem', color: '#666', lineHeight: 1.8, marginBottom: '2rem' }}>
                  {t.analyse.intro.p1}
                </p>
                <p style={{ fontSize: '1.1rem', color: '#666', lineHeight: 1.8, marginBottom: '2rem' }}>
                  {t.analyse.intro.p2}
                </p>
                <p style={{ fontSize: '1.1rem', color: '#666', lineHeight: 1.8 }}>
                  {t.analyse.intro.p3}
                </p>
              </motion.div>
            </Col>
            <Col lg={6}>
              <Row className="g-4">
                {mainServices.map((service, index) => (
                  <Col md={12} key={index}>
                    <motion.div initial={{ opacity: 0, x: 50 }} animate={introInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: index * 0.2 }} whileHover={{ y: -5, transition: { duration: 0.3 } }}>
                      <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <div style={{ background: `linear-gradient(135deg, ${service.color}, ${service.color}dd)`, padding: '1.5rem', color: 'white' }}>
                          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>{service.icon}</div>
                          <h4 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>{service.title}</h4>
                          <p style={{ margin: 0, opacity: 0.9 }}>{service.description}</p>
                        </div>
                      </Card>
                    </motion.div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── APPROACH ── */}
      <section ref={setApproachRef} style={{ padding: '5rem 0' }}>
        <Container>
          <Row><Col lg={12} className="text-center mb-5">
            <motion.h2 initial={{ opacity: 0, y: 30 }} animate={approachInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} style={{ fontSize: '2.5rem', fontWeight: 700, color: '#333', marginBottom: '1rem' }}>
              {t.analyse.approach.title}
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={approachInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }} style={{ fontSize: '1.2rem', color: '#666' }}>
              {t.analyse.approach.subtitle}
            </motion.p>
          </Col></Row>
          <Row className="g-4">
            {approachSteps.map((step, index) => (
              <Col lg={3} md={6} key={index}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={approachInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.2 }} whileHover={{ y: -10, transition: { duration: 0.3 } }} style={{ background: '#fff', borderRadius: '20px', padding: '2rem', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', position: 'relative', height: '100%' }}>
                  <div style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg, #d90000, #ff4444)', color: 'white', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 700, boxShadow: '0 4px 15px rgba(217,0,0,0.3)' }}>{step.step}</div>
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ color: '#d90000', fontSize: '2.5rem', marginBottom: '1rem' }}>{step.icon}</div>
                    <h4 style={{ color: '#333', fontWeight: 700, marginBottom: '1rem' }}>{step.title}</h4>
                    <p style={{ color: '#666', margin: 0 }}>{step.description}</p>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── EVALUATION AREAS ── */}
      <section ref={setEvaluationsRef} style={{ padding: '5rem 0', background: '#f8f9fa' }}>
        <Container>
          <Row><Col lg={12} className="text-center mb-5">
            <motion.h2 initial={{ opacity: 0, y: 30 }} animate={evaluationsInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} style={{ fontSize: '2.5rem', fontWeight: 700, color: '#333', marginBottom: '1rem' }}>
              {t.analyse.evaluations.title}
            </motion.h2>
          </Col></Row>
          <Row className="g-5">
            {evaluationAreas.map((area, index) => (
              <Col lg={12} key={index}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={evaluationsInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: index * 0.2 }}>
                  <Card style={{ border: 'none', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                    <Row className="g-0 align-items-center">
                      <Col lg={6} className={index % 2 === 1 ? 'order-lg-2' : ''}>
                        <div style={{ height: '400px', backgroundImage: `url(${area.image})`, backgroundSize: 'cover', backgroundPosition: 'center', position: 'relative' }}>
                          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: area.color, fontSize: '4rem', background: 'rgba(255,255,255,0.9)', borderRadius: '50%', width: '100px', height: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>{area.icon}</div>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div style={{ padding: '3rem' }}>
                          <h3 style={{ color: area.color, fontSize: '2rem', fontWeight: 700, marginBottom: '1.5rem' }}>{area.title}</h3>
                          <p style={{ color: '#666', lineHeight: 1.8, marginBottom: '2rem', textAlign: 'justify' }}>{area.description}</p>
                          <ul style={{ listStyle: 'none', padding: 0 }}>
                            {area.features.map((feature, idx) => (
                              <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8rem' }}>
                                <FaCheckCircle style={{ color: area.color, marginRight: '0.8rem', fontSize: '1rem' }} />
                                <span style={{ color: '#666' }}>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── DELIVERABLES ── */}
      <section ref={setDeliverablesRef} style={{ padding: '5rem 0' }}>
        <Container>
          <Row><Col lg={12} className="text-center mb-5">
            <motion.h2 initial={{ opacity: 0, y: 30 }} animate={deliverablesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} style={{ fontSize: '2.5rem', fontWeight: 700, color: '#333', marginBottom: '1rem' }}>
              {t.analyse.deliverables.title}
            </motion.h2>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={deliverablesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }} style={{ fontSize: '1.2rem', color: '#666' }}>
              {t.analyse.deliverables.subtitle}
            </motion.p>
          </Col></Row>
          <Row className="g-4">
            {deliverablesList.map((item, index) => (
              <Col lg={3} md={6} key={index}>
                <motion.div initial={{ opacity: 0, y: 50 }} animate={deliverablesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6, delay: index * 0.1 }} whileHover={{ y: -5, transition: { duration: 0.3 } }}>
                  <Card style={{ height: '100%', border: 'none', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                    <Card.Body style={{ padding: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ background: 'linear-gradient(135deg, #d90000, #ff4444)', color: 'white', width: '70px', height: '70px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '1.8rem' }}>
                          {item.icon}
                        </div>
                        <h4 style={{ color: '#333', fontWeight: 700, marginBottom: '1rem', fontSize: '1.2rem' }}>{item.title}</h4>
                      </div>
                      <p style={{ color: '#666', margin: 0, lineHeight: 1.6, fontSize: '0.95rem' }}>{item.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── CONSEIL ── */}
      <section id="conseil" style={{ padding: '5rem 0', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container>
          <Row><Col lg={12} className="text-center mb-5">
            <motion.h2 initial={{ opacity: 0, y: 30 }} animate={deliverablesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }} style={{ fontSize: '2.5rem', fontWeight: 700, color: '#333', marginBottom: '1rem' }}
              dangerouslySetInnerHTML={{ __html: `${t.analyse.conseil.title.split('Conseil')[0]}<span style="color:#d90000">Conseil</span>${t.analyse.conseil.title.split('Conseil')[1] || ''}` }} />
            <motion.p initial={{ opacity: 0, y: 20 }} animate={deliverablesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.2 }} style={{ fontSize: '1.2rem', color: '#666', maxWidth: '800px', margin: '0 auto' }}>
              {t.analyse.conseil.subtitle}
            </motion.p>
          </Col></Row>

          <Row className="g-5">
            {/* Conseil Stratégique */}
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, x: -50 }} animate={deliverablesInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.3 }}>
                <Card style={{ border: 'none', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', height: '100%', background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)' }}>
                  <Card.Body style={{ padding: '3rem' }}>
                    <div style={{ background: 'linear-gradient(135deg, #d90000, #ff4444)', color: 'white', width: '80px', height: '80px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2rem', fontSize: '2.5rem' }}><FaLightbulb /></div>
                    <h3 style={{ color: '#333', fontSize: '1.8rem', fontWeight: 700, marginBottom: '1.5rem' }}>{t.analyse.conseil.strat.title}</h3>
                    <p style={{ color: '#666', lineHeight: 1.8, marginBottom: '2rem', fontSize: '1.1rem' }}>{t.analyse.conseil.strat.description}</p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {t.analyse.conseil.strat.list.map((item, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8rem' }}>
                          <FaCheckCircle style={{ color: '#d90000', marginRight: '0.8rem', fontSize: '1rem' }} />
                          <span style={{ color: '#666' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>

            {/* Conseil Technique */}
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, x: 50 }} animate={deliverablesInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.5 }}>
                <Card style={{ border: 'none', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', height: '100%', background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)' }}>
                  <Card.Body style={{ padding: '3rem' }}>
                    <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #3b82f6)', color: 'white', width: '80px', height: '80px', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2rem', fontSize: '2.5rem' }}><FaCog /></div>
                    <h3 style={{ color: '#333', fontSize: '1.8rem', fontWeight: 700, marginBottom: '1.5rem' }}>{t.analyse.conseil.tech.title}</h3>
                    <p style={{ color: '#666', lineHeight: 1.8, marginBottom: '2rem', fontSize: '1.1rem' }}>{t.analyse.conseil.tech.description}</p>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                      {t.analyse.conseil.tech.list.map((item, idx) => (
                        <li key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8rem' }}>
                          <FaCheckCircle style={{ color: '#0ea5e9', marginRight: '0.8rem', fontSize: '1rem' }} />
                          <span style={{ color: '#666' }}>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>

          {/* Processus de Conseil */}
          <Row className="mt-5">
            <Col lg={12}>
              <motion.div initial={{ opacity: 0, y: 50 }} animate={deliverablesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.7 }}>
                <Card style={{ border: 'none', borderRadius: '20px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
                  <Card.Body style={{ padding: '3rem' }}>
                    <h3 style={{ color: '#333', fontSize: '2rem', fontWeight: 700, marginBottom: '2rem', textAlign: 'center' }}>{t.analyse.conseil.process.title}</h3>
                    <Row className="g-4">
                      {processSteps.map((step, index) => (
                        <Col lg={3} md={6} key={index}>
                          <div style={{ textAlign: 'center', padding: '1.5rem', position: 'relative' }}>
                            <div style={{ background: 'linear-gradient(135deg, #d90000, #ff4444)', color: 'white', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '1.3rem', fontWeight: 700, boxShadow: '0 4px 15px rgba(217,0,0,0.3)' }}>{step.step}</div>
                            <div style={{ color: '#d90000', fontSize: '2rem', marginBottom: '1rem' }}>{step.icon}</div>
                            <h4 style={{ color: '#333', fontWeight: 700, marginBottom: '0.8rem', fontSize: '1.2rem' }}>{step.title}</h4>
                            <p style={{ color: '#666', margin: 0, fontSize: '0.95rem', lineHeight: 1.5 }}>{step.description}</p>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>

          {/* Pourquoi S2i - Advantages */}
          <Row className="mt-5">
            <Col lg={12} className="text-center">
              <motion.div initial={{ opacity: 0, y: 30 }} animate={deliverablesInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.9 }} style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: '24px', padding: '4rem 3rem', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.15)' }}>
                <div style={{ background: 'linear-gradient(135deg, rgba(217,0,0,0.2), rgba(217,0,0,0.1))', borderRadius: '20px', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem auto', position: 'relative', zIndex: 1, border: '2px solid rgba(217,0,0,0.3)' }}>
                  <FaLightbulb style={{ fontSize: '2.2rem', color: '#d90000' }} />
                </div>
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '2.5rem', color: '#ffffff', lineHeight: '1.2' }}>
                    {t.analyse.conseil.advantages.title}
                  </h3>
                  <Row className="g-4">
                    {advantagesList.map((adv, index) => (
                      <Col lg={3} md={6} key={index}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '2.5rem', marginBottom: '1rem', color: 'rgba(255,255,255,0.9)' }}>{adv.icon}</div>
                          <h4 style={{ fontWeight: 700, marginBottom: '0.8rem', fontSize: '1.2rem', color: '#ffffff' }}>{adv.title}</h4>
                          <p style={{ margin: 0, opacity: 0.9, fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)', lineHeight: '1.6' }}>{adv.description}</p>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  <div className="d-flex flex-wrap justify-content-center gap-3" style={{ marginTop: '2.5rem' }}>
                    <Button onClick={() => navigate('/#contact')} style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', background: 'linear-gradient(135deg, #ffffff, #f8fafc)', color: '#1e293b', border: 'none', boxShadow: '0 4px 16px rgba(255,255,255,0.2)' }}>
                      <FaPhoneAlt style={{ marginRight: '8px' }} />
                      {t.analyse.conseil.advantages.btn}
                    </Button>
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default AnalyseConseil;